/**
 * Test Bundle Builder
 * Handles powder variant selection and cart integration for the IWTT
 * (i-want-to-test-bundle) bundle. The One-time / Subscribe & Save
 * toggle, validation message, and CTA all live in the bundle-buy-card
 * snippet; this module drives that card via window.paBundleBuyCard[uid]:
 *   - setEnabled(bool) / setValidationMessage(text) when flavor selection
 *     changes
 *   - onCtaClick(planId): build the two-line cart payload (cookie variety
 *     box + chosen powder, qty 1 each), append selling_plan to each item
 *     if subscribed, post to /cart/add.
 */

(function() {
  'use strict';

  // Namespace for the bundle builder
  window.TestBundleBuilder = window.TestBundleBuilder || {};

  /**
   * Test Bundle Builder Class
   */
  class BundleBuilder {
    constructor(containerId) {
      this.containerId = containerId;
      this.container = document.getElementById(containerId);
      if (!this.container) {
        console.error('Test Bundle Builder: Container not found:', containerId);
        return;
      }

      // Load data from window object
      this.data = window.testBundleData && window.testBundleData[containerId];
      if (!this.data) {
        console.error('Test Bundle Builder: Data not found for:', containerId);
        return;
      }

      // State
      this.selectedProteinVariant = null;

      // DOM Elements (just the variant pills + error slot — the rest of
      // the buy UI is owned by the bundle-buy-card snippet).
      this.variantButtons = this.container.querySelectorAll('.variant-btn');
      this.errorMessage = this.container.querySelector('.test-bundle-error-message');

      // bundle-buy-card API. Initialised lazily — the snippet's IIFE may
      // run after this constructor depending on script order. We poll
      // briefly in init() rather than racing.
      this.cardApi = null;

      // Initialize
      this.init();
    }

    init() {
      this.bindEvents();
      this.connectBuyCard(() => {
        this.validateSelections();
      });
    }

    /**
     * Wait (briefly) for the bundle-buy-card snippet's IIFE to register
     * its API, then run the callback. Resolves immediately if available.
     */
    connectBuyCard(cb) {
      var attempts = 0;
      var maxAttempts = 50; // 50 × 20ms = 1s
      var self = this;
      function tryConnect() {
        var api = window.paBundleBuyCard && window.paBundleBuyCard[self.containerId];
        if (api) {
          self.cardApi = api;
          // Register click handler for the buy card's CTA. planId is ""
          // for one-time, "<id>" when subscribed.
          api.onCtaClick(function (planId) { self.handleAddToCart(planId); });
          if (cb) cb();
          return;
        }
        if (++attempts < maxAttempts) {
          setTimeout(tryConnect, 20);
        }
      }
      tryConnect();
    }

    /**
     * Bind event listeners for things this module owns: just the powder
     * variant buttons.
     */
    bindEvents() {
      this.variantButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          if (!btn.disabled) {
            this.selectVariant(btn);
          }
        });
      });
    }

    /**
     * Select a variant for protein powder
     */
    selectVariant(button) {
      const variantId = parseInt(button.dataset.variantId);
      const variantImage = button.dataset.variantImage;

      this.selectedProteinVariant = {
        id: variantId,
        price: parseInt(button.dataset.variantPrice),
        title: button.dataset.variantTitle
      };

      // Update button UI
      this.variantButtons.forEach(btn => {
        btn.classList.remove('selected');
      });
      button.classList.add('selected');

      // Update product image if available
      if (variantImage) {
        const productRow = button.closest('.single-product-row');
        const img = productRow && productRow.querySelector('.single-product-row__main-image');
        if (img) {
          img.src = variantImage;
        }
      }

      this.validateSelections();
    }

    /**
     * Validate that all required selections are made and drive the buy
     * card's enabled state + validation slot accordingly.
     */
    validateSelections() {
      const isValid = this.selectedProteinVariant !== null;
      const translations = this.data.translations || {};
      const message = isValid
        ? ''
        : (translations.please_select_flavor || 'Please select a protein powder flavor');

      if (this.cardApi) {
        this.cardApi.setEnabled(isValid);
        this.cardApi.setValidationMessage(message);
      }

      return isValid;
    }

    /**
     * CTA click handler — buy card has already gated the click on the
     * disabled state, so selectedProteinVariant is non-null here. planId
     * is the empty string for one-time, otherwise the chosen plan ID.
     */
    async handleAddToCart(planId) {
      if (!this.validateSelections()) return;

      // Disable CTA during the round-trip; re-enable in finally.
      if (this.cardApi) this.cardApi.setEnabled(false);

      try {
        await this.addBundleToCart(planId);

        document.dispatchEvent(new CustomEvent('cart:update'));

        // Refresh + open the custom cart drawer if it's mounted.
        if (window.paCart && typeof window.paCart.refresh === 'function') {
          window.paCart.refresh();
          if (typeof window.paCart.open === 'function') window.paCart.open();
        } else {
          // Fall back to Dawn's cart drawer if present.
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }
      } catch (error) {
        console.error('Failed to add bundle to cart:', error);
        this.showError(error.message || 'Failed to add to cart. Please try again.');
      } finally {
        if (this.cardApi) this.cardApi.setEnabled(true);
      }
    }

    /**
     * Build and post the two-line cart payload. planId is "" for
     * one-time, "<id>" for subscribed.
     */
    async addBundleToCart(planId) {
      const items = [];
      const qty = 1;

      // Add cookie variety box
      const cookieVarietyBox = this.data.products.cookieVarietyBox;
      if (cookieVarietyBox && cookieVarietyBox.variantId) {
        items.push({
          id: parseInt(cookieVarietyBox.variantId),
          quantity: qty
        });
      }

      // Add selected protein powder variant. No line item properties on
      // either component — keeping them clean lets Shopify merge with
      // matching standalone-product lines (same variant + same selling
      // plan + no properties = one line, qty incremented).
      if (this.selectedProteinVariant) {
        items.push({
          id: parseInt(this.selectedProteinVariant.id),
          quantity: qty
        });
      }

      if (items.length === 0) {
        throw new Error('No items to add');
      }

      // Apply selling plan to every line when subscribed.
      if (planId) {
        items.forEach(item => { item.selling_plan = planId; });
      }

      // Check for UpCart (legacy — being phased out)
      const isUpCartActive = !!(
        window.UpCart ||
        window.upcart ||
        window.Upcart ||
        document.querySelector('script[src*="upcart"]') ||
        document.querySelector('link[href*="upcart"]')
      );

      const cart = !isUpCartActive && (
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer')
      );

      // Build FormData
      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        if (item.selling_plan) {
          formData.append(`items[${index}][selling_plan]`, item.selling_plan);
        }
        if (item.properties) {
          Object.entries(item.properties).forEach(([key, value]) => {
            formData.append(`items[${index}][properties][${key}]`, String(value));
          });
        }
      });

      // Build URL with sections for cart drawer update
      let url = '/cart/add';
      if (!isUpCartActive && cart && typeof cart.getSectionsToRender === 'function') {
        const sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsUrl = window.location.pathname;
        url += `?sections=${encodeURIComponent(sections)}&sections_url=${encodeURIComponent(sectionsUrl)}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      // Retry without sections param if it fails
      if (!response.ok && response.status === 400 && url.includes('sections=')) {
        const retryResponse = await fetch('/cart/add', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });

        if (!retryResponse.ok) {
          const errorText = await retryResponse.text();
          throw new Error(errorText || 'Failed to add to cart');
        }

        return await retryResponse.json();
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to add to cart');
      }

      const data = await response.json();

      // Update Dawn cart drawer if present (UpCart updates itself).
      if (!isUpCartActive && cart && data.sections) {
        cart.renderContents(data);
      }

      return data;
    }

    /**
     * Show error message
     */
    showError(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorMessage.style.display = 'block';

        setTimeout(() => {
          this.errorMessage.style.display = 'none';
        }, 5000);
      }
    }
  }

  /**
   * Initialize a test bundle builder instance
   */
  window.TestBundleBuilder.init = function(containerId) {
    return new BundleBuilder(containerId);
  };

})();
